import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CriminalRecordIndexEntry } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner } from '../../ui/components';

const CRO_ROLES = ['CRO_OFFICER'];

/** Police — Criminal Record Index (M05): restricted, heavily audited, NIN-keyed lookup only (no browsing). */
export function CroRecords() {
  const { persona } = useAuth();
  const isCroOfficer = persona ? persona.roles.some((r) => CRO_ROLES.includes(r)) : false;
  const [nin, setNin] = useState('');
  const [searchedNin, setSearchedNin] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const recordsQ = useApiQuery<{ items: CriminalRecordIndexEntry[] }>(
    searchedNin ? `/api/v1/police/cro/records?nin=${encodeURIComponent(searchedNin)}` : null,
  );

  return (
    <div>
      <PageHeader
        title="Criminal Record Index"
        subtitle="Restricted — every read and write is audited. Lookup is NIN-keyed only, no browsing (M05)."
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSearchedNin(nin.trim());
            }}
          >
            <input className="field-input !w-56 font-mono" placeholder="NIN, e.g. 108-4471-2-2-19" value={nin} onChange={(e) => setNin(e.target.value)} />
            <button type="submit" className="btn-primary" disabled={!nin.trim()}>
              Search
            </button>
            {isCroOfficer ? (
              <button type="button" className="btn-secondary" onClick={() => setShowAdd(true)}>
                + Add record
              </button>
            ) : null}
          </form>
        }
      />

      {!isCroOfficer ? <RoleNotice roles={CRO_ROLES} /> : null}

      {!searchedNin ? (
        <div className="card">
          <EmptyState title="Enter a NIN to search" hint="No general browsing is permitted for this restricted index." />
        </div>
      ) : (
        <>
          {recordsQ.error ? <ErrorNote message={recordsQ.error} /> : null}
          <div className="card overflow-x-auto">
            {recordsQ.loading ? (
              <Spinner />
            ) : (recordsQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title={`No CRO records on file for ${searchedNin}`} />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Offence code</th>
                    <th className="table-head">Offence date</th>
                    <th className="table-head">Court</th>
                    <th className="table-head">Disposition</th>
                    <th className="table-head">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {recordsQ.data!.items.map((r) => (
                    <tr key={r.id}>
                      <td className="table-cell font-medium">{r.offenceCode}</td>
                      <td className="table-cell">{formatDate(r.offenceDate)}</td>
                      <td className="table-cell">{r.court}</td>
                      <td className="table-cell text-xs text-slate-500">{r.disposition}</td>
                      <td className="table-cell text-xs text-slate-400">{r.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {showAdd ? (
        <AddRecordModal
          defaultNin={searchedNin ?? nin}
          onClose={() => setShowAdd(false)}
          onDone={() => {
            setShowAdd(false);
            if (searchedNin) recordsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AddRecordModal({ defaultNin, onClose, onDone }: { defaultNin: string; onClose: () => void; onDone: () => void }) {
  const [ninValue, setNinValue] = useState(defaultNin);
  const [offenceCode, setOffenceCode] = useState('');
  const [offenceDate, setOffenceDate] = useState('');
  const [court, setCourt] = useState('');
  const [disposition, setDisposition] = useState('');
  const [convictionRef, setConvictionRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/police/cro/records', {
        nin: ninValue.trim(),
        offenceCode: offenceCode.trim(),
        offenceDate,
        court: court.trim(),
        disposition: disposition.trim(),
        ...(convictionRef.trim() ? { convictionRef: convictionRef.trim() } : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add Criminal Record Index entry" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="NIN">
            <input className="field-input font-mono" value={ninValue} onChange={(e) => setNinValue(e.target.value)} />
          </Field>
          <Field label="Offence code">
            <input className="field-input" value={offenceCode} onChange={(e) => setOffenceCode(e.target.value)} placeholder="e.g. THEFT_S260" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Offence date">
            <input className="field-input" type="date" value={offenceDate} onChange={(e) => setOffenceDate(e.target.value)} />
          </Field>
          <Field label="Court">
            <input className="field-input" value={court} onChange={(e) => setCourt(e.target.value)} placeholder="e.g. Supreme Court of Seychelles" />
          </Field>
        </div>
        <Field label="Disposition">
          <textarea className="field-input" rows={2} value={disposition} onChange={(e) => setDisposition(e.target.value)} placeholder="e.g. Convicted, 6 months suspended sentence" />
        </Field>
        <Field label="Conviction reference (optional)">
          <input className="field-input" value={convictionRef} onChange={(e) => setConvictionRef(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !ninValue.trim() || !offenceCode.trim() || !offenceDate || !court.trim() || disposition.trim().length < 2}
            onClick={submit}
          >
            {busy ? 'Recording…' : 'Record entry'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
